// ── Heatmap — календарь занятий по дням и прогноз повторений ───────────────────

import { useEffect, useRef, useState } from "react";
import { t, uiLocale } from "../i18n";
import Icon from "../Icon";
import { SANS } from "../themes";
import { usePalette } from "../ui/theme";
import { calcStats, getActivityData, localDateKey, nCards, nDays } from "../utils";
import { floatSurface } from '../ui/surface';

// ── Heatmap — тепловая карта активности ──────────────────────────────────────

export function Heatmap({theme,forecast,lang}){
  const C=usePalette();
  // forecast: {dateKey: count} for future SRS-due cards
  const now=new Date();now.setHours(0,0,0,0);
  const curYear=now.getFullYear();
  const [year,setYear]=useState(curYear);
  const [tooltip,setTooltip]=useState(null); // {x,y,text}
  const containerRef=useRef(null);
  const [containerW,setContainerW]=useState(400);
  useEffect(()=>{
    const update=()=>{if(containerRef.current)setContainerW(containerRef.current.offsetWidth||400);};
    update();
    const ro=typeof ResizeObserver!=="undefined"?new ResizeObserver(update):null;
    if(ro&&containerRef.current)ro.observe(containerRef.current);
    window.addEventListener("resize",update);
    return()=>{ro?.disconnect();window.removeEventListener("resize",update);};
  },[]);

  const activity=getActivityData(lang);
  const stats=calcStats(activity);
  const todayKey=localDateKey(now);

  // Colors: past=green gradient, future=grey gradient (like Anki)
  const PAL={
    light:{empty:"#ecebe7",past:["#9be9a8","#40c463","#30a14e","#216e39"],future:["#c6cdd6","#9ba7b4","#6e7d8e","#455060"],text:"#57606a"},
    dark: {empty:"#2f2f2d",past:["#0e4429","#006d32","#26a641","#39d353"],future:["#2d3f50","#3d5570","#4e6d90","#5e85b0"],text:"#768390"},
    eye:  {empty:"#d9caa0",past:["#c49a2e","#a07000","#7a5200","#5a3a00"],future:["#c0b080","#a09060","#807040","#5a4e20"],text:"#665c54"},
  };
  const pal=PAL[theme]||PAL.light;

  // Build year grid
  const jan1=new Date(year,0,1);
  const gridStart=new Date(jan1);gridStart.setDate(jan1.getDate()-jan1.getDay()); // start from Sunday of week containing Jan 1
  const weeks=[];
  for(let w=0;w<54;w++){
    const week=[];
    for(let d=0;d<7;d++){
      const date=new Date(gridStart);date.setDate(gridStart.getDate()+w*7+d);
      if(date.getFullYear()>year&&date.getMonth()>0)break;
      const key=localDateKey(date);
      week.push({date,key,inYear:date.getFullYear()===year});
    }
    if(week.length&&week.some(x=>x.inYear))weeks.push(week);
  }

  // Compute max for scaling
  const yearActivity=weeks.flat().filter(d=>d.inYear&&d.key<=todayKey).map(d=>activity[d.key]||0);
  const maxAct=Math.max(1,...yearActivity.filter(Boolean));
  const yearForecast=weeks.flat().filter(d=>d.inYear&&d.key>todayKey).map(d=>(forecast||{})[d.key]||0);
  const maxFor=Math.max(1,...yearForecast.filter(Boolean));

  const getCellColor=(key)=>{
    // "YYYY-MM-DD" → local-midnight Date (NOT new Date(key), which parses as UTC).
    const [Y,M,D]=key.split("-").map(Number);
    const date=new Date(Y,M-1,D);
    if(!key||date.getFullYear()!==year)return"transparent";
    if(date>now){
      const cnt=(forecast||{})[key]||0;
      if(!cnt)return pal.empty;
      return pal.future[Math.min(3,Math.floor((cnt/maxFor)*4))];
    }
    const cnt=activity[key]||0;
    if(!cnt)return pal.empty;
    return pal.past[Math.min(3,Math.floor((cnt/maxAct)*4))];
  };

  // Даты и подписи — на языке интерфейса.
  const getTooltipText=(key,date)=>{
    const isFuture=date>now;
    const label=date.toLocaleDateString(uiLocale(),{weekday:"long",day:"numeric",month:"long",year:"numeric"});
    if(isFuture){
      const cnt=(forecast||{})[key]||0;
      return cnt?t("{0}: к повторению {1}",[label,nCards(cnt)]):t("{0}: ничего не запланировано",[label]);
    }
    const cnt=activity[key]||0;
    return cnt?t("{0}: повторено {1}",[label,nCards(cnt)]):t("{0}: без повторений",[label]);
  };

  const MNAMES=Array.from({length:12},(_,m)=>new Date(2024,m,1).toLocaleDateString(uiLocale(),{month:"short"}).replace(".",""));
  const WNAMES=Array.from({length:7},(_,d)=>new Date(2023,0,1+d).toLocaleDateString(uiLocale(),{weekday:"short"}).replace(".","")); // 1 янв 2023 — воскресенье
  const monthLabels=[];
  weeks.forEach((week,wi)=>{
    const first=week.find(d=>d.inYear);if(!first)return;
    const prev=wi>0?weeks[wi-1].find(d=>d.inYear):null;
    if(!prev||first.date.getMonth()!==prev.date.getMonth())monthLabels.push({wi,label:MNAMES[first.date.getMonth()]});
  });

  const DAY_LBL=28;
  const CELL=Math.max(8,Math.min(13,Math.floor((containerW-DAY_LBL-4)/weeks.length)));
  const GAP=Math.max(2,Math.round(CELL/5));
  const STEP=CELL+GAP;
  const svgW=DAY_LBL+weeks.length*STEP;
  const svgH=14+7*STEP;

  return(
    <div style={{padding:"12px 14px 10px",borderTop:`1px solid ${C.border}`,position:"relative"}}>
      {/* Header row with year nav */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <span style={{fontSize:11,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:".07em"}}><Icon n="chart" style={{marginRight:6}}/>{t("Активность")}</span>
        <div style={{display:"flex",alignItems:"center",gap:4}}>
          <button data-btn="" onClick={()=>setYear(y=>y-1)} aria-label={t("Предыдущий год")} title={t("Предыдущий год")} className="ac-ibtn" style={{background:C.lift?C.surface:"none",border:`1px solid ${C.border}`,color:C.mid,width:32,height:32,borderRadius:8,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",padding:0}}><Icon n="chevronLeft" s={16}/></button>
          <button data-btn="" onClick={()=>setYear(curYear)} title={t("Текущий год")} disabled={year===curYear} style={{minWidth:52,height:32,borderRadius:8,background:"none",border:"none",color:year===curYear?C.text:C.blueDark,fontSize:13,fontWeight:600,cursor:year===curYear?"default":"pointer",fontFamily:SANS,fontVariantNumeric:"tabular-nums",padding:"0 6px"}}>{year}</button>
          <button data-btn="" onClick={()=>setYear(y=>Math.min(y+1,curYear))} disabled={year>=curYear} aria-label={t("Следующий год")} title={t("Следующий год")} className="ac-ibtn" style={{background:C.lift?C.surface:"none",border:`1px solid ${C.border}`,color:year>=curYear?C.border:C.mid,width:32,height:32,borderRadius:8,cursor:year>=curYear?"default":"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",padding:0}}><Icon n="chevronRight" s={16}/></button>
        </div>
      </div>
      {/* Tooltip */}
      {tooltip&&(
        <div style={{position:"absolute",bottom:"calc(100% + 4px)",left:"50%",transform:"translateX(-50%)",
          ...floatSurface(C),borderRadius:12,padding:"5px 10px",
          fontSize:12,color:C.text,whiteSpace:"nowrap",pointerEvents:"none",zIndex:50,
          boxShadow:`0 4px 12px ${C.shadowMd}`}}>
          {tooltip}
        </div>
      )}
      {/* Grid */}
      <div ref={containerRef} style={{width:"100%",overflow:"hidden"}}>
        <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{display:"block",overflow:"visible"}}>
          {monthLabels.map(({wi,label})=>(
            <text key={wi} x={DAY_LBL+wi*STEP} y={10} fontSize={10.5} fill={pal.text} fontFamily={SANS}>{label}</text>
          ))}
          {[1,3,5].map(i=>(
            <text key={i} x={DAY_LBL-2} y={14+i*STEP+CELL-1} fontSize={9.5} fill={pal.text} textAnchor="end" fontFamily={SANS}>
              {WNAMES[i]}
            </text>
          ))}
          {weeks.map((week,wi)=>week.map((day,di)=>day.inYear?(
            <rect key={`${wi}-${di}`}
              x={DAY_LBL+wi*STEP} y={14+di*STEP} width={CELL} height={CELL}
              rx={Math.max(2,Math.round(CELL/4))}
              fill={getCellColor(day.key)}
              stroke={day.key===todayKey?pal.past[3]:"none"}
              strokeWidth={day.key===todayKey?1.5:0}
              style={{cursor:"pointer"}}
              onMouseEnter={()=>setTooltip(getTooltipText(day.key,day.date))}
              onMouseLeave={()=>setTooltip(null)}
            />
          ):null))}
        </svg>
      </div>
      {/* Stats row — Anki style */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:4,marginTop:8,paddingTop:8,borderTop:`1px solid ${C.borderSoft}`}}>
        {[
          [t("В среднем"),`${stats.dailyAvg}`,C.blueDark],
          [t("Дней с занятиями"),`${stats.daysLearned}%`,C.green],
          [t("Рекорд"),nDays(stats.longestStreak),C.warmDark],
          [t("Серия"),nDays(stats.currentStreak),stats.currentStreak>0?C.green:C.faint],
        ].map(([label,val,color])=>(
          <div key={label} style={{textAlign:"center",padding:"4px 2px",background:C.surface,borderRadius:9,border:`1px solid ${C.borderSoft}`}}>
            <div style={{fontSize:11.5,color:C.faint,marginBottom:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</div>
            <div style={{fontSize:13,fontWeight:700,color,fontVariantNumeric:"tabular-nums"}}>{val}</div>
          </div>
        ))}
      </div>

      {/* Next 7 days forecast — Anki-style "сколько прилетит завтра/послезавтра/..." */}
      {(() => {
        const DOW = [t("Вс"),t("Пн"),t("Вт"),t("Ср"),t("Чт"),t("Пт"),t("Сб")];
        const today = new Date(); today.setHours(0,0,0,0);
        const days = Array.from({length:7}, (_,i) => {
          const d = new Date(today); d.setDate(today.getDate()+i+1);
          const k = localDateKey(d);
          return {date:d, key:k, count:(forecast||{})[k]||0};
        });
        const maxC = Math.max(1, ...days.map(d=>d.count));
        const total = days.reduce((s,d)=>s+d.count,0);
        if (!Object.keys(forecast||{}).length) return null;
        return (
          <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${C.borderSoft}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
              <span style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:".07em"}}><Icon n="calendar" style={{marginRight:6}}/>{t("Прогноз 7 дней")}</span>
              <span style={{fontSize:10,color:C.faint}}>{t("всего")} {total}</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,alignItems:"end"}}>
              {days.map((d,i)=>{
                const h = d.count>0 ? Math.max(4, Math.round((d.count/maxC)*32)) : 2;
                return (
                  <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}
                       title={t("{0} {1}: {2} карт",[DOW[d.date.getDay()],d.date.getDate(),d.count])}>
                    <span style={{fontSize:9,color:d.count?C.text:C.faint,fontWeight:600,lineHeight:1}}>{d.count||"·"}</span>
                    <div style={{width:"100%",height:34,display:"flex",alignItems:"flex-end"}}>
                      <div style={{width:"100%",height:h,background:d.count?C.blue:C.border,borderRadius:"2px 2px 0 0",opacity:d.count?0.85:0.4}}/>
                    </div>
                    <span style={{fontSize:9,color:C.faint,lineHeight:1}}>{DOW[d.date.getDay()]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
